import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import {
  ACCESS_TOKEN_KEY,
  clearTokens,
  decodeUser,
  getAccess,
  getRefresh,
  onTenantChange,
  orgOf,
  setTokens,
} from '@/lib/auth';
import type { SessionUser } from '@/lib/auth';
import { setOnboardingToken } from '@/lib/onboarding';

/** One organization the signed-in user belongs to. */
export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  name_ar: string | null;
  status: string;
  locale: string;
  base_currency: string;
  modules: string[];
  role: string;
  trial_ends_at: string | null;
  /** Tenant accent colour (hex), or null to use Railit ember. Applied at runtime
   *  to `--primary` by <BrandAccent> — see src/lib/brand.ts. */
  brand_primary: string | null;
  logo_url: string | null;
}

/** What the current subscription allows. Drives the billing banners and gates. */
export interface Entitlements {
  status: string;
  plan_code: string;
  seats: number;
  seats_used: number;
  addons: string[];
  can_read: boolean;
  can_write: boolean;
  /**
   * Is there a live Stripe subscription with a payment method behind it?
   *
   * Deliberately distinct from `status === 'TRIALING'`: a tenant can be inside
   * their trial having already subscribed (Stripe simply starts billing when
   * the trial ends), and must not be nagged to subscribe again.
   */
  subscribed: boolean;
  trial_ends_at: string | null;
  current_period_end: string | null;
  reason?: string;
  price: { usd: number; bhd: number };
  /**
   * While a payment is being retried: the instant writing stops.
   *
   * Sent by the server rather than computed here. The banner, the dialog and
   * the dunning email all quote this deadline, and a client that worked it out
   * from `current_period_end` plus its own copy of the grace constant is a
   * countdown that disagrees with the email telling the customer the same
   * thing.
   */
  write_until: string | null;
  grace_days_left: number | null;
  /** Past the grace window: one screen only, until it is paid. */
  locked: boolean;
  /** Dormant after the hold period. Restorable, never deleted. */
  on_hold: boolean;
  hold_at: string | null;
  /** What bringing a held workspace back costs, on top of a new plan. */
  restore_fee_usd: number;
  /**
   * AI credits left this month, and whether that is worth a warning.
   *
   * Sent with the session rather than fetched per component: the menubar badge
   * and the chat's out-of-credits card both need it, and two mounts each
   * calling /usage is two round trips and a warning that arrives after the
   * screen it belongs to.
   */
  credits_remaining: number | null;
  credits_low: boolean;
  /** Trialing with nothing paid for yet. Drives the activation offer. */
  open_trial: boolean;
}

interface SessionResponse {
  access: string;
  refresh?: string;
  user?: SessionUser;
  org?: OrgSummary;
  organizations?: OrgSummary[];
}

interface MeResponse {
  user: SessionUser;
  org: OrgSummary | null;
  organizations: OrgSummary[];
  entitlements: Entitlements;
}

export interface SignupInput {
  name: string;
  email: string;
  password: string;
  company_name: string;
  company_name_ar?: string;
  locale?: 'en' | 'ar';
}

export interface AuthContextValue {
  user: SessionUser | null;
  org: OrgSummary | null;
  organizations: OrgSummary[];
  entitlements: Entitlements | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  /** Is this product switched on for the current organization? */
  hasModule: (code: string) => boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  /**
   * Take up a session minted somewhere other than the login form — the last
   * step of onboarding, and the return leg of "Continue with Google". Goes
   * through the same path as login so the query cache is cleared exactly once.
   */
  adoptSession: (res: SessionResponse) => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<SessionUser | null>(() => decodeUser(getAccess()));
  const [org, setOrg] = useState<OrgSummary | null>(null);
  const [organizations, setOrganizations] = useState<OrgSummary[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => Boolean(getAccess()));

  // The org this tab believes it is in, readable from the storage listener
  // without making it depend on (and re-subscribe to) `org`.
  const orgRef = useRef<string | null>(null);
  useEffect(() => {
    orgRef.current = org?.id ?? orgOf(getAccess());
  }, [org]);

  // Two switches in quick succession race: without a generation stamp the
  // SLOWER /auth/me can land last and repaint the previous tenant's name,
  // colour and entitlements over the current one's data.
  const refreshGeneration = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    const generation = (refreshGeneration.current += 1);
    const isStale = () => refreshGeneration.current !== generation;

    const token = getAccess();
    if (!token) {
      setUser(null);
      setOrg(null);
      setOrganizations([]);
      setEntitlements(null);
      return;
    }
    try {
      const me = await api<MeResponse>('/auth/me');
      if (isStale()) return;
      setUser(me.user);
      setOrg(me.org);
      setOrganizations(me.organizations ?? []);
      setEntitlements(me.entitlements ?? null);
    } catch (err) {
      if (isStale()) return;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearTokens();
        setUser(null);
        setOrg(null);
        setOrganizations([]);
        setEntitlements(null);
      } else {
        // Network hiccup — keep the optimistic user decoded from the token so
        // the shell still renders instead of bouncing to /login. Entitlements
        // and the org list are NOT kept: they describe a specific tenant, and
        // holding the previous one's is how a workspace with no subscription
        // came to render as subscribed.
        setUser((prev) => prev ?? decodeUser(token));
        setEntitlements(null);
        setOrganizations([]);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!getAccess()) {
      setIsBootstrapping(false);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      await refresh();
      if (!cancelled) {
        setIsBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  /**
   * Safety net for every tenant change this component did not initiate.
   *
   * Two of them exist. The silent refresh in lib/api.ts mints a token for the
   * server session's active org, which can differ from what this tab shows.
   * And the tokens live in localStorage, shared by every tab: switching org in
   * one tab rewrites the token the others are using, so a second tab keeps
   * rendering the old company's name, role and colours while its next request
   * — including any write — goes to the new one. Following the switch is the
   * honest answer, because the server keeps a single active org per session:
   * the other tab genuinely is in the new tenant now.
   */
  useEffect(() => {
    const onChanged = () => {
      queryClient.clear();
      setEntitlements(null);
      setUser(decodeUser(getAccess()));
      void refresh();
    };

    const unsubscribe = onTenantChange(onChanged);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== ACCESS_TOKEN_KEY) return;
      const next = orgOf(getAccess());
      if (next !== orgRef.current) onChanged();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  const adopt = useCallback(
    (res: SessionResponse, remember?: boolean) => {
      setTokens(res.access, res.refresh ?? null, remember, { silent: true });
      // A fresh session must never read the previous one's cache.
      queryClient.clear();
      setUser(res.user ?? decodeUser(res.access));
      if (res.org) setOrg(res.org);
      if (res.organizations) setOrganizations(res.organizations);
    },
    [],
  );

  const login = useCallback(
    async (email: string, password: string, remember = true): Promise<void> => {
      const res = await api<SessionResponse & { needs_workspace?: boolean; onboarding?: string }>(
        '/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
          headers: { 'Content-Type': 'application/json' },
        },
      );

      /**
       * Correct password, but the sign-up wizard was abandoned before a
       * workspace existed. There is no session to adopt — the server hands back
       * an onboarding token instead, and the honest thing is to drop them back
       * where they stopped rather than say their account does not work.
       */
      if (res.needs_workspace && res.onboarding) {
        setOnboardingToken(res.onboarding);
        navigate('/onboarding', { replace: true });
        return;
      }

      adopt(res, remember);
      await refresh();
      navigate('/dashboard', { replace: true });
    },
    [adopt, navigate, refresh],
  );

  const signup = useCallback(
    async (input: SignupInput): Promise<void> => {
      const res = await api<SessionResponse>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      });
      adopt(res, true);
      await refresh();
      navigate('/dashboard', { replace: true });
    },
    [adopt, navigate, refresh],
  );

  /**
   * Move to another organization. The server mints a fresh access token for it
   * — the client never picks the tenant a request runs against.
   *
   * The cache MUST be emptied on the way through. Query keys are things like
   * ['billing'] and ['org','members'] with no tenant in them, so react-query
   * would happily serve the previous company's data against the new one — which
   * is how a workspace with no subscription came to display "Subscribed". The
   * server was right the whole time; the client was showing a neighbour's
   * answer. Scoping every key by org id would work too, but clearing is the one
   * change that cannot be forgotten in a future query.
   */
  const adoptSession = useCallback(
    async (res: SessionResponse): Promise<void> => {
      adopt(res, true);
      await refresh();
      navigate('/dashboard', { replace: true });
    },
    [adopt, navigate, refresh],
  );

  const switchOrg = useCallback(
    async (orgId: string): Promise<void> => {
      const res = await api<{ access: string; org: OrgSummary }>('/auth/switch-org', {
        method: 'POST',
        body: JSON.stringify({ org_id: orgId, refresh: getRefresh() ?? undefined }),
        headers: { 'Content-Type': 'application/json' },
      });
      setTokens(res.access, null, undefined, { silent: true });
      queryClient.clear();
      // `user` carries the role INSIDE the org, and entitlements describe the
      // org's subscription — both belong to the tenant we just left. Drop them
      // now rather than letting /auth/me overwrite them a round trip later,
      // because in between, useCan() and RequireRole would be answering for the
      // wrong company.
      setUser(decodeUser(res.access));
      setEntitlements(null);
      setOrg(res.org);
      await refresh();
      navigate('/dashboard', { replace: true });
    },
    [navigate, refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    // Best-effort server-side revocation so the refresh token can no longer
    // rotate; local state is cleared regardless of the call's outcome.
    const refreshToken = getRefresh();
    try {
      await api('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh: refreshToken ?? undefined }),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      // ignore — revoke is best-effort
    }
    clearTokens();
    queryClient.clear();
    setUser(null);
    setOrg(null);
    setOrganizations([]);
    setEntitlements(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const hasModule = useCallback(
    (code: string) => {
      // Before /me lands we optimistically allow, so the sidebar does not
      // flicker items in on first paint.
      if (!org) return true;
      return org.modules.includes(code);
    },
    [org],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      org,
      organizations,
      entitlements,
      isAuthenticated: Boolean(user),
      isBootstrapping,
      hasModule,
      login,
      signup,
      adoptSession,
      switchOrg,
      logout,
      refresh,
    }),
    [
      user,
      org,
      organizations,
      entitlements,
      isBootstrapping,
      hasModule,
      login,
      signup,
      adoptSession,
      switchOrg,
      logout,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
